// ─────────────────────────────────────────────────────────────────────────────
// The Alba mark.
//
// TWO PLACES ONLY: the sidebar header and the dashboard. That restraint is the
// point — a logo repeated on every screen stops being a logo and becomes
// wallpaper. If a third call site ever appears, it is almost certainly the
// wrong answer to a different problem.
//
// THE MARK, NEVER THE WORDMARK. The logo is the symbol from the brand
// guideline — the abstract tooth that also reads as a heart, formed from the A
// and D of DENTAL. The word "Alba" set as type is not the logo and was a
// placeholder that stood here until the real artwork arrived.
//
// Transparent PNG, so it takes the colour of whatever it sits on rather than
// carrying its own ground. Imported rather than referenced from public/, so
// Vite fingerprints it and a replacement can never be served from a stale
// cache — the icons in public/ are the opposite case and need stable paths.
//
// The alt text is the company name from i18n rather than the word "logo", so a
// screen reader announces what it identifies, not what kind of object it is.
//
// `tone="inverse"` REVERSES the mark for a dark ground. The artwork is sage,
// which on the near-black sidebar measures 4.60:1 — above the 3:1 WCAG asks of
// a meaningful graphic, and still wrong, because the mark is a thin outline at
// 26px and a thin sage stroke on near-black is barely there. Rather than filter
// or approximate it, the PNG's own alpha channel is used as a mask and filled
// with the sidebar ink: the same mark, reversed, which is what every brand
// guideline means by a mono version. One prop to revert if the guideline
// forbids it.
// ─────────────────────────────────────────────────────────────────────────────

import type { ReactElement } from "react";
import markUrl from "../assets/alba-mark.png";
import { useI18n } from "../i18n";

export function AlbaLogo({ height, className, tone = "brand" }: {
  readonly height: number;
  readonly className?: string;
  /** `inverse` fills the mark with the sidebar ink, for a dark ground. */
  readonly tone?: "brand" | "inverse";
}): ReactElement {
  const { t } = useI18n();

  if (tone === "inverse") {
    // A masked box rather than an <img>, so the fill is a real colour token and
    // not an approximation of one. `role`/`aria-label` because a masked span
    // carries no alt text of its own.
    return (
      <span
        role="img"
        aria-label={t("common.appName")}
        className={className}
        style={{
          display: "block",
          height,
          width: height,
          backgroundColor: "var(--sidebar-ink)",
          maskImage: `url(${markUrl})`,
          WebkitMaskImage: `url(${markUrl})`,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskSize: "contain",
          WebkitMaskSize: "contain",
          maskPosition: "center",
          WebkitMaskPosition: "center",
        }}
      />
    );
  }

  return (
    <img
      src={markUrl}
      alt={t("common.appName")}
      className={className}
      style={{ height, width: "auto", display: "block" }}
    />
  );
}
